import React, { useState, useEffect, useMemo } from 'react';
import { Button, List, Typography, Input, Select, message, Spin, Modal, Tag, Tooltip, Empty, Dropdown } from 'antd';
import { ClockCircleOutlined, SwapOutlined, ProjectOutlined, MoreOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { fetchAdminTaskList, fetchEmployeesList, fetchAdminAddTask, fetchEmployeeTaskList, fetchUnclaimedTaskList, fetchTakeTask, fetchEditTask, fetchAdminEditTask, fetchSelfEvaluationDetail, fetchSelfEvaluation } from '../../fetch/fetch';
import ManageTaskListItem from '../manage/ManageTaskListItem';

const { Text } = Typography;
const { Option } = Select;

type RoleItem = { name: string; description: string };

function normalizeRoles(input: (string | RoleItem)[]): RoleItem[] {
  return input.map(item => {
    if (typeof item === 'string') {
      return { name: item.trim(), description: '' };
    }
    return item;
  });
}

function formatEstimatedHours(estimated: number | string | undefined | null): string {
  if (estimated === undefined || estimated === null || estimated === '') return '0 h';
  const n = typeof estimated === 'string' ? parseFloat(estimated.replace(',', '.')) : Number(estimated);
  if (Number.isNaN(n)) return '0 h';
  const h = Math.floor(n);
  const m = Math.round((n - h) * 60);
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

function parseEstimatedHours(estimated: number | string | undefined | null): number {
  if (estimated === undefined || estimated === null || estimated === '') return 0;
  const n = typeof estimated === 'string' ? parseFloat(estimated.replace(',', '.')) : Number(estimated);
  if (Number.isNaN(n)) return 0;
  return n;
}

function sumEstimatedHours(tasks: any[]): number {
  return tasks.reduce((sum, task) => sum + parseEstimatedHours(task?.estimated_hrs), 0);
}

const HOUR_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '0.25', label: '15 min' },
  { value: '0.5', label: '30 min' },
  { value: '0.75', label: '45 min' },
  { value: '1', label: '1 h' },
  { value: '1.25', label: '1 h 15 min' },
  { value: '1.5', label: '1 h 30 min' },
  { value: '1.75', label: '1 h 45 min' },
  { value: '2', label: '2 h' },
  { value: '2.25', label: '2 h 15 min' },
  { value: '2.5', label: '2 h 30 min' },
  { value: '2.75', label: '2 h 45 min' },
  { value: '3', label: '3 h' },
  { value: '3.25', label: '3 h 15 min' },
  { value: '3.5', label: '3 h 30 min' },
  { value: '3.75', label: '3 h 45 min' },
  { value: '4', label: '4 h' },
  { value: '4.25', label: '4 h 15 min' },
  { value: '4.5', label: '4 h 30 min' },
  { value: '4.75', label: '4 h 45 min' },
  { value: '5', label: '5 h' },
  { value: '5.25', label: '5 h 15 min' },
  { value: '5.5', label: '5 h 30 min' },
  { value: '5.75', label: '5 h 45 min' },
  { value: '6', label: '6 h' },
  { value: '6.25', label: '6 h 15 min' },
  { value: '6.5', label: '6 h 30 min' },
  { value: '6.75', label: '6 h 45 min' },
  { value: '7', label: '7 h' },
  { value: '7.25', label: '7 h 15 min' },
  { value: '7.5', label: '7 h 30 min' },
  { value: '7.75', label: '7 h 45 min' },
  { value: '8', label: '8 h' },
];

const LogsEmptyIcon: React.FC = () => (
  <svg width="155" height="89" viewBox="0 0 155 89" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M50.7522 66.2675L110.428 56.416L85.5484 50.0937H65.955H0.200195L19.028 69.0795L50.7522 66.2675Z" fill="#DFDFDB" />
    <path d="M49.8701 88.0911L122.355 56.416L97.4752 50.0937H77.8818H12.127L49.8701 88.0911Z" fill="#DFDFDB" />
    <path d="M38.8031 2.09421L116.444 4.23726L129.918 75.6778L50.6076 87.0472L47.6055 65.4422L38.8031 2.09421Z" fill="#FBFBF7" />
    <path d="M38.8031 2.09421L21.2734 68.0168L47.6055 65.4422L38.8031 2.09421Z" fill="#FBFBF7" />
    <path d="M38.8031 2.09421L116.444 4.23726L129.918 75.6778L50.6076 87.0472L47.6055 65.4422M38.8031 2.09421L21.2734 68.0168L47.6055 65.4422M38.8031 2.09421L47.6055 65.4422" stroke="#DFDFDB" stroke-width="2.9598" />
    <path d="M38.5468 6.4506L29.9463 68.0741H47.6834L38.5468 6.4506Z" fill="#DFDFDB" />
    <path d="M84.2781 43.2327L88.0414 33.174L94.6637 43.0447L106.332 44.5344L99.0501 52.0575L102.667 62.7635L91.7199 58.375L82.0266 64.6443L82.3531 52.8821L73.105 45.0166L84.2781 43.2327Z" fill="#DFDFDB" />
    <path d="M41.7451 21.9463L119.575 20.6311" stroke="#DFDFDB" stroke-width="2.9598" />
  </svg>
);

const MOOD_SELF_EVAL_COPY: { title: string; description: string }[] = [
  { title: 'Low energy today', description: 'I could use some support - I’m not feeling fully confident in my delivery.' },
  { title: 'Below my potential', description: 'I’m contributing, but not at my full potential today.' },
  { title: 'Steady and reliable', description: 'I’m delivering as expected and supporting team outcomes reliably.' },
  { title: 'Proactive or improving', description: 'I’m actively enhancing outcomes and finding ways to work more efficiently.' },
  { title: 'Driving meaningful impact', description: 'Creating measurable improvements and moving things forward!' },
];


const MOOD_ICONS: React.ReactNode[] = [
  // First 4 mood icons (as provided by you)
  <svg key="m1" width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.25 0C13.0361 0 9.89429 0.953046 7.22199 2.73862C4.54969 4.52419 2.46689 7.06209 1.23696 10.0314C0.00704087 13.0007 -0.314763 16.268 0.312247 19.4202C0.939256 22.5724 2.48692 25.4679 4.75952 27.7405C7.03213 30.0131 9.9276 31.5607 13.0798 32.1878C16.232 32.8148 19.4993 32.493 22.4686 31.263C25.4379 30.0331 27.9758 27.9503 29.7614 25.278C31.547 22.6057 32.5 19.4639 32.5 16.25C32.4955 11.9416 30.7819 7.81102 27.7355 4.76454C24.689 1.71806 20.5584 0.00454972 16.25 0ZM10.625 11.25C10.9958 11.25 11.3584 11.36 11.6667 11.566C11.975 11.772 12.2154 12.0649 12.3573 12.4075C12.4992 12.7501 12.5363 13.1271 12.464 13.4908C12.3916 13.8545 12.2131 14.1886 11.9508 14.4508C11.6886 14.713 11.3545 14.8916 10.9908 14.964C10.6271 15.0363 10.2501 14.9992 9.90748 14.8573C9.56486 14.7154 9.27203 14.475 9.066 14.1667C8.85997 13.8584 8.75001 13.4958 8.75001 13.125C8.75001 12.6277 8.94755 12.1508 9.29918 11.7992C9.65081 11.4475 10.1277 11.25 10.625 11.25ZM23.125 24.8312C22.8382 24.9967 22.4974 25.0416 22.1775 24.9561C21.8576 24.8705 21.5847 24.6615 21.4188 24.375C20.2516 22.3578 18.4172 21.25 16.25 21.25C14.0828 21.25 12.2484 22.3594 11.0813 24.375C11.0031 24.5244 10.8956 24.6564 10.7651 24.7631C10.6346 24.8699 10.4838 24.9491 10.3219 24.9961C10.16 25.0431 9.99028 25.0568 9.82292 25.0365C9.65557 25.0161 9.49405 24.9622 9.3481 24.8778C9.20214 24.7934 9.07476 24.6804 8.97361 24.5455C8.87247 24.4107 8.79965 24.2567 8.75954 24.093C8.71944 23.9292 8.71286 23.7591 8.74022 23.5927C8.76759 23.4264 8.82831 23.2673 8.91876 23.125C10.5266 20.3453 13.1984 18.75 16.25 18.75C19.3016 18.75 21.9734 20.3438 23.5813 23.125C23.7467 23.4118 23.7916 23.7526 23.7061 24.0725C23.6205 24.3924 23.4115 24.6653 23.125 24.8312ZM21.875 15C21.5042 15 21.1417 14.89 20.8333 14.684C20.525 14.478 20.2846 14.1851 20.1427 13.8425C20.0008 13.4999 19.9637 13.1229 20.036 12.7592C20.1084 12.3955 20.287 12.0614 20.5492 11.7992C20.8114 11.537 21.1455 11.3584 21.5092 11.286C21.8729 11.2137 22.2499 11.2508 22.5925 11.3927C22.9351 11.5346 23.228 11.775 23.434 12.0833C23.64 12.3916 23.75 12.7542 23.75 13.125C23.75 13.6223 23.5525 14.0992 23.2008 14.4508C22.8492 14.8025 22.3723 15 21.875 15Z" fill="currentColor" />
  </svg>,
  <svg key="m2" width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.25 0C13.0361 0 9.89429 0.953046 7.22199 2.73862C4.54969 4.52419 2.46689 7.06209 1.23696 10.0314C0.00704087 13.0007 -0.314763 16.268 0.312247 19.4202C0.939256 22.5724 2.48692 25.4679 4.75952 27.7405C7.03213 30.0131 9.9276 31.5607 13.0798 32.1878C16.232 32.8148 19.4993 32.493 22.4686 31.263C25.4379 30.0331 27.9758 27.9503 29.7614 25.278C31.547 22.6057 32.5 19.4639 32.5 16.25C32.4946 11.9419 30.7809 7.81175 27.7346 4.76545C24.6883 1.71915 20.5581 0.00537508 16.25 0ZM10.625 11.25C10.9958 11.25 11.3584 11.36 11.6667 11.566C11.975 11.772 12.2154 12.0649 12.3573 12.4075C12.4992 12.7501 12.5363 13.1271 12.464 13.4908C12.3916 13.8545 12.2131 14.1886 11.9508 14.4508C11.6886 14.713 11.3545 14.8916 10.9908 14.964C10.6271 15.0363 10.2501 14.9992 9.90748 14.8573C9.56486 14.7154 9.27203 14.475 9.066 14.1667C8.85997 13.8584 8.75001 13.4958 8.75001 13.125C8.75001 12.6277 8.94755 12.1508 9.29918 11.7992C9.65081 11.4475 10.1277 11.25 10.625 11.25ZM22.5 22.5H10C9.66849 22.5 9.35054 22.3683 9.11612 22.1339C8.8817 21.8995 8.75001 21.5815 8.75001 21.25C8.75001 20.9185 8.8817 20.6005 9.11612 20.3661C9.35054 20.1317 9.66849 20 10 20H22.5C22.8315 20 23.1495 20.1317 23.3839 20.3661C23.6183 20.6005 23.75 20.9185 23.75 21.25C23.75 21.5815 23.6183 21.8995 23.3839 22.1339C23.1495 22.3683 22.8315 22.5 22.5 22.5ZM21.875 15C21.5042 15 21.1417 14.89 20.8333 14.684C20.525 14.478 20.2846 14.1851 20.1427 13.8425C20.0008 13.4999 19.9637 13.1229 20.036 12.7592C20.1084 12.3955 20.287 12.0614 20.5492 11.7992C20.8114 11.537 21.1455 11.3584 21.5092 11.286C21.8729 11.2137 22.2499 11.2508 22.5925 11.3927C22.9351 11.5346 23.228 11.775 23.434 12.0833C23.64 12.3916 23.75 12.7542 23.75 13.125C23.75 13.6223 23.5525 14.0992 23.2008 14.4508C22.8492 14.8025 22.3723 15 21.875 15Z" fill="currentColor" />
  </svg>,
  <svg xmlns="http://www.w3.org/2000/svg" width="33" height="33" viewBox="0 0 33 33" fill="none">
    <path d="M16.25 0C13.0361 0 9.89429 0.953046 7.22199 2.73862C4.54969 4.52419 2.46689 7.06209 1.23696 10.0314C0.00704087 13.0007 -0.314763 16.268 0.312247 19.4202C0.939256 22.5724 2.48692 25.4679 4.75952 27.7405C7.03213 30.0131 9.9276 31.5607 13.0798 32.1878C16.232 32.8148 19.4993 32.493 22.4686 31.263C25.4379 30.0331 27.9758 27.9503 29.7614 25.278C31.547 22.6057 32.5 19.4639 32.5 16.25C32.4955 11.9416 30.7819 7.81102 27.7355 4.76454C24.689 1.71806 20.5584 0.00454972 16.25 0ZM10.625 11.25C10.9958 11.25 11.3584 11.36 11.6667 11.566C11.975 11.772 12.2154 12.0649 12.3573 12.4075C12.4992 12.7501 12.5363 13.1271 12.464 13.4908C12.3916 13.8545 12.2131 14.1886 11.9508 14.4508C11.6886 14.713 11.3545 14.8916 10.9908 14.964C10.6271 15.0363 10.2501 14.9992 9.90748 14.8573C9.56486 14.7154 9.27203 14.475 9.066 14.1667C8.85997 13.8584 8.75001 13.4958 8.75001 13.125C8.75001 12.6277 8.94755 12.1508 9.29918 11.7992C9.65081 11.4475 10.1277 11.25 10.625 11.25ZM23.5813 20.625C21.9734 23.4047 19.3016 25 16.25 25C13.1984 25 10.5266 23.4062 8.91876 20.625C8.82831 20.4827 8.76759 20.3236 8.74022 20.1573C8.71286 19.9909 8.71944 19.8208 8.75954 19.657C8.79965 19.4933 8.87247 19.3393 8.97361 19.2045C9.07476 19.0696 9.20214 18.9566 9.3481 18.8722C9.49405 18.7878 9.65557 18.7339 9.82292 18.7135C9.99028 18.6932 10.16 18.7069 10.3219 18.7539C10.4838 18.8009 10.6346 18.8801 10.7651 18.9869C10.8956 19.0936 11.0031 19.2256 11.0813 19.375C12.2484 21.3922 14.0828 22.5 16.25 22.5C18.4172 22.5 20.2516 21.3906 21.4188 19.375C21.4969 19.2256 21.6045 19.0936 21.7349 18.9869C21.8654 18.8801 22.0162 18.8009 22.1781 18.7539C22.34 18.7069 22.5097 18.6932 22.6771 18.7135C22.8444 18.7339 23.006 18.7878 23.1519 18.8722C23.2979 18.9566 23.4253 19.0696 23.5264 19.2045C23.6275 19.3393 23.7004 19.4933 23.7405 19.657C23.7806 19.8208 23.7872 19.9909 23.7598 20.1573C23.7324 20.3236 23.6717 20.4827 23.5813 20.625ZM21.875 15C21.5042 15 21.1417 14.89 20.8333 14.684C20.525 14.478 20.2846 14.1851 20.1427 13.8425C20.0008 13.4999 19.9637 13.1229 20.036 12.7592C20.1084 12.3955 20.287 12.0614 20.5492 11.7992C20.8114 11.537 21.1455 11.3584 21.5092 11.286C21.8729 11.2137 22.2499 11.2508 22.5925 11.3927C22.9351 11.5346 23.228 11.775 23.434 12.0833C23.64 12.3916 23.75 12.7542 23.75 13.125C23.75 13.6223 23.5525 14.0992 23.2008 14.4508C22.8492 14.8025 22.3723 15 21.875 15Z" fill="currentColor" />
  </svg>,
  <svg xmlns="http://www.w3.org/2000/svg" width="33" height="33" viewBox="0 0 33 33" fill="none">
    <path d="M16.2506 0C20.5589 0.00465704 24.6896 1.71826 27.736 4.76465C30.7824 7.81111 32.4961 11.9417 32.5006 16.25C32.5006 19.4639 31.547 22.606 29.7614 25.2783C27.9758 27.9505 25.4376 30.0328 22.4684 31.2627C19.4992 32.4925 16.2319 32.8145 13.0798 32.1875C9.92768 31.5604 7.03197 30.0128 4.75944 27.7402C2.48699 25.4677 0.939157 22.572 0.312173 19.4199C-0.314744 16.2678 0.00709882 13.0004 1.23698 10.0312C2.4669 7.06197 4.55004 4.52385 7.22233 2.73828C9.89458 0.952806 13.0368 0 16.2506 0ZM7.73405 19.583C9.06738 22.983 12.3673 25.417 16.2506 25.417C20.1339 25.4169 23.4173 22.9829 24.7673 19.583H7.73405ZM22.5924 11.3926C22.2499 11.2508 21.873 11.2138 21.5094 11.2861C21.1458 11.3585 20.8117 11.5367 20.5495 11.7988C20.2873 12.061 20.1082 12.3952 20.0358 12.7588C19.9635 13.1225 20.0013 13.5002 20.1432 13.8428C20.2851 14.1852 20.5255 14.4776 20.8337 14.6836C21.142 14.8896 21.5048 15 21.8756 15C22.3726 14.9999 22.8493 14.8025 23.2008 14.4512C23.5525 14.0995 23.7506 13.6223 23.7506 13.125C23.7506 12.7542 23.6403 12.3913 23.4342 12.083C23.2282 11.7748 22.935 11.5345 22.5924 11.3926ZM10.6256 11.25C10.1285 11.25 9.65109 11.4473 9.29948 11.7988C8.94785 12.1505 8.75065 12.6277 8.75065 13.125C8.75065 13.4958 8.86005 13.8587 9.06608 14.167C9.2721 14.4753 9.56531 14.7155 9.90788 14.8574C10.2504 14.9993 10.6273 15.0362 10.9909 14.9639C11.3545 14.8915 11.6887 14.7132 11.9508 14.4512C12.213 14.189 12.3921 13.8548 12.4645 13.4912C12.5369 13.1275 12.499 12.7498 12.3571 12.4072C12.2152 12.0648 11.9749 11.7724 11.6667 11.5664C11.3584 11.3605 10.9963 11.2501 10.6256 11.25Z" fill="currentColor" />
  </svg>,
  // 5th mood button (keep default for now)
  <svg
    key="m5"
    xmlns="http://www.w3.org/2000/svg"
    width="33"
    height="33"
    viewBox="0 0 33 33"
    fill="none"
  >
    <path
      d="M16.2506 0C20.5588 0.00468402 24.6896 1.71829 27.736 4.76465C30.7823 7.8111 32.4961 11.9417 32.5006 16.25C32.5006 19.4639 31.5469 22.606 29.7614 25.2783C27.9758 27.9504 25.4376 30.0328 22.4684 31.2627C19.4992 32.4925 16.2318 32.8145 13.0798 32.1875C9.92771 31.5605 7.03196 30.0127 4.75944 27.7402C2.487 25.4677 0.939159 22.572 0.312173 19.4199C-0.314744 16.2678 0.00709882 13.0004 1.23698 10.0312C2.46691 7.06199 4.55006 4.52384 7.22233 2.73828C9.89456 0.952837 13.0368 0 16.2506 0ZM7.73405 19.583C9.06739 22.983 12.3673 25.417 16.2506 25.417C20.1338 25.4168 23.4173 22.9829 24.7673 19.583H7.73405ZM10.6247 6.25C10.5314 6.25006 10.4398 6.27663 10.3503 6.33008C10.2607 6.38358 10.1946 6.46433 10.152 6.57129L9.19401 8.95215L6.71549 9.17969C6.59669 9.19718 6.50268 9.23706 6.43424 9.2998C6.36586 9.36256 6.31462 9.44336 6.28092 9.54102C6.24734 9.6386 6.24128 9.73923 6.26237 9.8418C6.28355 9.94434 6.33651 10.0361 6.42155 10.1162L8.29948 11.8145L7.73795 14.3428C7.71241 14.459 7.7208 14.5638 7.76335 14.6572C7.80593 14.7507 7.8654 14.8292 7.94206 14.8916C8.01867 14.9539 8.10848 14.9891 8.21061 14.998C8.31274 15.0069 8.41093 14.9804 8.50456 14.918L10.6247 13.5801L12.7458 14.918C12.8394 14.9807 12.9375 15.0073 13.0397 14.998C13.1418 14.9887 13.2317 14.9536 13.3083 14.8916C13.3849 14.8295 13.4444 14.751 13.487 14.6572C13.5295 14.5635 13.5379 14.4586 13.5124 14.3428L12.9499 11.8145L14.8278 10.1162C14.9133 10.0363 14.9668 9.94453 14.988 9.8418C15.009 9.73918 15.0024 9.63901 14.9684 9.54102C14.9344 9.44292 14.8832 9.36223 14.8151 9.2998C14.747 9.23746 14.653 9.19751 14.5339 9.17969L12.0563 8.95215L11.0973 6.57129C11.0547 6.46428 10.9887 6.38358 10.8991 6.33008C10.8096 6.27669 10.7179 6.25 10.6247 6.25ZM21.8747 6.25C21.7814 6.25005 21.6898 6.27662 21.6003 6.33008C21.5107 6.38358 21.4446 6.46432 21.402 6.57129L20.444 8.95215L17.9655 9.17969C17.8467 9.19718 17.7527 9.23706 17.6842 9.2998C17.6158 9.36256 17.5646 9.44336 17.5309 9.54102C17.4973 9.63861 17.4913 9.73923 17.5124 9.8418C17.5336 9.94434 17.5865 10.0361 17.6715 10.1162L19.5495 11.8145L18.988 14.3428C18.9624 14.459 18.9708 14.5638 19.0133 14.6572C19.0559 14.7507 19.1154 14.8292 19.1921 14.8916C19.2687 14.9539 19.3585 14.9891 19.4606 14.998C19.5627 15.0069 19.6609 14.9803 19.7546 14.918L21.8747 13.5801L23.9958 14.918C24.0894 14.9807 24.1875 15.0073 24.2897 14.998C24.3918 14.9887 24.4817 14.9536 24.5583 14.8916C24.6349 14.8295 24.6944 14.751 24.737 14.6572C24.7795 14.5635 24.7879 14.4586 24.7624 14.3428L24.1999 11.8145L26.0778 10.1162C26.1633 10.0363 26.2168 9.94453 26.238 9.8418C26.259 9.73918 26.2524 9.639 26.2184 9.54102C26.1844 9.44292 26.1332 9.36223 26.0651 9.2998C25.997 9.23745 25.903 9.19751 25.7839 9.17969L23.3053 8.95215L22.3473 6.57129C22.3047 6.46428 22.2387 6.38358 22.1491 6.33008C22.0596 6.27668 21.9679 6.25 21.8747 6.25Z"
      fill="currentColor"
    />
  </svg>,
];


const TaskList: React.FC<{
  isManage: boolean,
  auth: string,
  jwtToken: string,
  headerSelectedDate?: string,
  activeDate?: string,
  activeDateRange?: { start: string; end: string },
  timeZone?: string,
  onTasksChange?: (tasksCount: number) => void,
  onTotalHoursChange?: (totalHours: number) => void,
  selectedTaskId?: string,
  onlyActive?: boolean
}> = ({ isManage, auth, jwtToken, headerSelectedDate, activeDate, activeDateRange, onTasksChange, onTotalHoursChange, selectedTaskId, onlyActive }) => {
  const [tasks, setTasks] = useState<any>([]);
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 480;
  });

  const [visible, setVisible] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', hours: '', role: '', comments: '', roleDescription: '' });
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<any[]>([]);
  const navigate = useNavigate();

  const [addTaskDrawerVisible, setAddTaskDrawerVisible] = useState(false); // For Add Task Drawer
  const [addTaskForm, setAddTaskForm] = useState<any>({
    title: '', comments: '', member: [], task_id: '', need_role: "1",
    tags: [] as Array<{ name: string; description: string }>
  }); // New task form state
  const [unclaimedTaskList, setUnclaimedTaskList] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [roles, setRoles] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [descModalVisible, setDescModalVisible] = useState(false);
  const [roleList, setRoleList] = useState<RoleItem[]>([]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const MAX_TAGS = 15;
  const [previousDate, setPreviousDate] = useState<string | undefined>(undefined);
  const [selfRatingValue, setSelfRatingValue] = useState<number | undefined>(undefined);
  const [isEvaluated, setIsEvaluated] = useState<boolean>(false);
  const [selfRatingSubmitting, setSelfRatingSubmitting] = useState(false);
  const [mobileMoodModalVisible, setMobileMoodModalVisible] = useState(false);
  const [mobileMoodDraft, setMobileMoodDraft] = useState<number | undefined>(undefined);
  const [desktopMoodHover, setDesktopMoodHover] = useState<number | undefined>(undefined);

  const getRatingDescription = (rating: number): string => {
    const ratingMap: { [key: number]: string } = {
      1: 'Low energy today',
      2: 'Below my potential',
      3: 'Steady and reliable',
      4: 'Proactive or improving',
      5: 'Driving meaningful impact'
    };
    return ratingMap[rating] || '';
  };

  const submitMoodRating = async (moodValue: number): Promise<boolean> => {
    if (!headerSelectedDate) {
      message.error('Date is not available');
      return false;
    }
    if (!jwtToken) {
      message.error('Auth token is missing');
      return false;
    }
    const prevValue = selfRatingValue;
    setSelfRatingSubmitting(true);
    setSelfRatingValue(moodValue);
    try {
      const response = await fetchSelfEvaluation(jwtToken, {
        take_date: headerSelectedDate,
        rating: moodValue,
      });

      if (response.status === '1') {
        message.success('Mood saved successfully');
        setIsEvaluated(true);
        setSelfRatingValue(moodValue);
        const res = await fetchSelfEvaluationDetail(jwtToken, {
          take_date: headerSelectedDate,
        });
        const evaluated =
          res.status === '1' &&
          res.response &&
          !Array.isArray(res.response) &&
          res.response.rating;
        setIsEvaluated(!!evaluated);
        if (evaluated && res.response.rating) {
          setSelfRatingValue(Number(res.response.rating));
        }
        return true;
      }
      message.error(response.message || 'Failed to save mood');
      setSelfRatingValue(prevValue);
      return false;
    } catch (error: any) {
      console.error('Error saving mood:', error);
      message.error('Network error. Please try again.');
      setSelfRatingValue(prevValue);
      return false;
    } finally {
      setSelfRatingSubmitting(false);
    }
  };

  useEffect(() => {
    if (jwtToken) {
      if (isManage) {
        adminTask()
        fetchEmployeesList(jwtToken).then((res) => {
          if (res.status === "1") {
            setEmployees(res.response);
          } else if (res.status === "-1") {
            navigate('/logout');
          } else {
            setEmployees([]);
            message.error(res.message);
          }
        }).catch((error: any) => {
          setEmployees([]);
          console.log(error);
        });
      }
    }
  }, [jwtToken]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isManage && headerSelectedDate) {
      employeeTaskHandle()
      UnclaimedTaskListHandle()
      setPreviousDate(headerSelectedDate);

      // Check if current date has been evaluated
      if (jwtToken) {
        fetchSelfEvaluationDetail(jwtToken, {
          take_date: headerSelectedDate
        }).then((res) => {
          // Check if evaluation exists
          // response is an object with rating when data exists, or [] when no data
          const evaluated = res.status === "1" && res.response && !Array.isArray(res.response) && res.response.rating;
          setIsEvaluated(!!evaluated);

          // If evaluated, set the rating value (convert string to number)
          if (evaluated && res.response.rating) {
            setSelfRatingValue(Number(res.response.rating));
          } else {
            setSelfRatingValue(undefined);
          }
        }).catch((error) => {
          console.error('Error fetching self-evaluation detail:', error);
          setIsEvaluated(false);
        });
      }
    } else if (!isManage && headerSelectedDate && previousDate === undefined) {
      // Initial load
      employeeTaskHandle()
      UnclaimedTaskListHandle()
      setPreviousDate(headerSelectedDate);

      // Check if current date has been evaluated on initial load
      if (jwtToken) {
        fetchSelfEvaluationDetail(jwtToken, {
          take_date: headerSelectedDate
        }).then((res) => {
          // Check if evaluation exists
          // response is an object with rating when data exists, or [] when no data
          const evaluated = res.status === "1" && res.response && !Array.isArray(res.response) && res.response.rating;
          setIsEvaluated(!!evaluated);

          // If evaluated, set the rating value (convert string to number)
          if (evaluated && res.response.rating) {
            setSelfRatingValue(Number(res.response.rating));
          } else {
            setSelfRatingValue(undefined);
          }
        }).catch((error) => {
          console.error('Error fetching self-evaluation detail:', error);
          setIsEvaluated(false);
        });
      }
    }
  }, [headerSelectedDate, jwtToken])

  // If selfRatingValue exists (from click or API), we only show that one mood icon in UI.

  const downloadRange = useMemo(() => {
    if (activeDateRange?.start && activeDateRange?.end) {
      return { start: activeDateRange.start, end: activeDateRange.end };
    }
    const end = moment().startOf('day').format('YYYY-MM-DD');
    const start = moment().startOf('day').subtract(2, 'weeks').format('YYYY-MM-DD');
    return { start, end };
  }, [activeDateRange]);
  const UnclaimedTaskListHandle = () => {
    let params = {
      search_date: headerSelectedDate
    }
    fetchUnclaimedTaskList(jwtToken, params).then((res) => {
      if (res.status === "1") {
        setUnclaimedTaskList(res.response);
      } else if (res.status === "-1") {
        navigate('/logout');
      } else {
        message.error(res.message);
      }
    }).catch((error: any) => {
      console.log(error);
    });

  }

  const setInputConfirm = () => {
    if (submitLoading) return;

    const newTags = [...addTaskForm.tags];


    changeTagsHandle(newTags);
    setDescModalVisible(false);
  };
  const handleInputConfirm = () => {
    if (submitLoading) return;

    const name = inputValue.trim();
    const description = "";

    if (!name) {
      message.error("Function name is required");
      return;
    }

    if (addTaskForm.tags.length >= MAX_TAGS) {
      message.error(`You can only add up to ${MAX_TAGS} Functions.`);
      return;
    }

    if (addTaskForm.tags.some((t: any) => t.name === name)) {
      message.error("This Function name already exists");
      return;
    }

    const newTags = [...addTaskForm.tags];

    newTags.push({ name, description });

    changeTagsHandle(newTags);
    setInputValue('');
    setDescModalVisible(false);
  };
  const handleTagClose = (removedTag: any) => {

    if (submitLoading) {
      return
    }
    changeTagsHandle(addTaskForm.tags.filter((tag: any) => tag.name !== removedTag))
  };

  const changeTagsHandle = (List: any) => {
    if (isEditing) {
      setSubmitLoading(true);
      const taskPayload = {
        task_id: addTaskForm.task_id,
        roles: List
      };
      fetchAdminEditTask(jwtToken, taskPayload).then((res) => {
        setSubmitLoading(false);
        if (res.status === "1") {
          setTasks((prevTasks: any) =>
            prevTasks.map((item: any) =>
              item.task_id === addTaskForm.task_id
                ? {
                  ...item,
                  roles: addTaskForm.need_role === "1" ? List : [],
                }
                : item
            )
          );
        } else if (res.status === "-1") {
          navigate('/logout');
        } else {
          message.error(res.message);
        }
      }).catch((error: any) => {
        setSubmitLoading(false);
        console.log(error);
      });
    }
    setAddTaskForm({
      ...addTaskForm,
      tags: List,
    });
  }

  const adminTask = () => {
    setLoading(true);
    fetchAdminTaskList(jwtToken).then((res) => {
      setLoading(false);
      if (res.status === "1") {
        const updatedTasks = res.response.map((task: any) => {
          const formattedDate = moment(task.created_ts).format('MM/DD HH:mm:ss');
          return {
            ...task,
            created_ts: formattedDate,
          };
        });

        setTasks(updatedTasks);
        if (onTasksChange) {
          onTasksChange(updatedTasks.length);
        }
        if (onTotalHoursChange) {
          onTotalHoursChange(sumEstimatedHours(updatedTasks));
        }
      } else if (res.status === "-1") {
        navigate('/logout');
      } else {
        setTasks([]);
        if (onTasksChange) {
          onTasksChange(0);
        }
        if (onTotalHoursChange) {
          onTotalHoursChange(0);
        }
        message.error(res.message);
      }
    }).catch((error: any) => {
      setTasks([]);
      if (onTasksChange) {
        onTasksChange(0);
      }
      if (onTotalHoursChange) {
        onTotalHoursChange(0);
      }
      setLoading(false);
      console.log(error);
    });
  }

  const employeeTaskHandle = () => {
    setLoading(true);
    let params = {
      search_date: headerSelectedDate
    }
    fetchEmployeeTaskList(jwtToken, params).then((res) => {
      setLoading(false);
      if (res.status === "1") {
        const updatedTasks = res.response.map((task: any) => {
          const formattedDate = moment(task.created_ts).format('MM/DD HH:mm:ss');
          return {
            ...task,
            created_ts: formattedDate,
          };
        });

        setTasks(updatedTasks);
        if (onTasksChange) {
          onTasksChange(updatedTasks.length);
        }
        if (onTotalHoursChange) {
          onTotalHoursChange(sumEstimatedHours(updatedTasks));
        }
      } else if (res.status === "-1") {
        navigate('/logout');
      } else {
        setTasks([]);
        if (onTasksChange) {
          onTasksChange(0);
        }
        if (onTotalHoursChange) {
          onTotalHoursChange(0);
        }
        message.error(res.message);
      }
    }).catch((error: any) => {
      setTasks([]);
      if (onTasksChange) {
        onTasksChange(0);
      }
      if (onTotalHoursChange) {
        onTotalHoursChange(0);
      }
      setLoading(false);
      console.log(error);
    });
  }
  const handleDownload = async () => {
    let param = {
      search_start_date: downloadRange.start,
      search_end_date: downloadRange.end,
    };

    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${jwtToken}`);
      myHeaders.append("Content-Type", "application/json");
      const API_URL = 'https://z7addc12sl.execute-api.us-east-2.amazonaws.com/v1/dkj'
      // const API_URL = 'https://l9nd2oj8q9.execute-api.us-east-2.amazonaws.com/beta/dkj'
      const url = `${API_URL}/Admin/download`;

      // Sending a POST request with the parameters
      const response = await fetch(url, {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify(param),  // Sending parameters as a JSON string
        redirect: "follow",
      });

      // Check if the response is successful
      if (!response.ok) {
        throw new Error("File download failed");
      }

      // Get the Blob data (file)
      const blob = await response.blob();

      // Create a temporary download link
      const downloadUrl = URL.createObjectURL(blob);

      // Create an <a> element and simulate a click to trigger the download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `Project-Product (${downloadRange.start} - ${downloadRange.end})`;
      link.click();  // Trigger the download

      // Clean up the URL object after download
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      // Handle errors
      console.error('Error during file download:', error);
    }
  };

  const handleDeleteTask = (taskId: number) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this task?',
      centered: true, // This centers the modal
      okButtonProps: {
        style: {
          backgroundColor: '#0E1F27',
          borderColor: '#0E1F27',
          color: 'white',
        },
      },
      onOk: () => {
        setLoading(true);
        const taskPayload = {
          task_log_id: taskId,
          status: 'Deleted',
        };
        fetchEditTask(jwtToken, taskPayload).then((res) => {
          setLoading(false);
          if (res.status === '1') {
            message.success(res.message);
            employeeTaskHandle();
          } else {
            message.error(res.message);
          }
        }).catch((error: any) => {
          setLoading(false);
          console.log(error);
        });
      },
      onCancel() {
      },
    });
  };


  const handleAddTask = () => {
    if (!newTask.title || !newTask.hours || !newTask.comments) {
      alert('All fields are required!');
      return;
    }
    if (!newTask.role) {
      alert('Function is required for the selected project!');
      return;
    }
    setSubmitLoading(true);
    const taskPayload = {
      task_id: newTask.title,
      estimated_hrs: newTask.hours,
      take_date: headerSelectedDate,
      role: newTask.role,
      comment: newTask.comments
    };
    fetchTakeTask(jwtToken, taskPayload).then((res) => {
      setSubmitLoading(false);
      if (res.status === "1") {
        message.success('Add successfully');
        setNewTask({ title: '', hours: '', role: '', comments: '', roleDescription: '' });
        setVisible(false);
        employeeTaskHandle()
      } else if (res.status === "-1") {
        navigate('/logout');
      } else {
        message.error(res.message);
      }
    }).catch((error: any) => {
      setSubmitLoading(false);
      console.log(error);
    });
  };

  const handleAddNewTask = () => {
    if (!addTaskForm.title) {
      alert('All fields are required!');
      return;
    }

    if (addTaskForm.member.length === 0) {
      message.error('Please select at least one member.');
      return;
    }
    const needRoleEffective = addTaskForm.tags.length > 0 ? '1' : '0';
    setSubmitLoading(true);
    const taskPayload = {
      title: addTaskForm.title,
      comment: addTaskForm.comments,
      uids: addTaskForm.member,
      need_role: needRoleEffective,
      ...(addTaskForm.task_id && { task_id: addTaskForm.task_id }),
      ...(needRoleEffective === '1' ? { roles: addTaskForm.tags } : { roles: [] })
    };
    if (isEditing) {

      fetchAdminEditTask(jwtToken, taskPayload).then((res) => {
        setSubmitLoading(false);
        if (res.status === "1") {
          message.success(res.message);
          setAddTaskForm({ title: '', comments: '', member: [], task_id: "", need_role: "1", tags: [] });
          setAddTaskDrawerVisible(false);
          setTasks((prevTasks: any) =>
            prevTasks.map((item: any) =>
              item.task_id === addTaskForm.task_id
                ? {
                  ...item,
                  title: addTaskForm.title,
                  comments: addTaskForm.comments,
                  assigned_to: addTaskForm.member.map((uid: any) => ({
                    assigned_to: uid,
                  })),
                  need_role: needRoleEffective,
                  roles: needRoleEffective === '1' ? addTaskForm.tags : [],
                }
                : item
            )
          );
        } else if (res.status === "-1") {
          navigate('/logout');
        } else {
          message.error(res.message);
        }
      }).catch((error: any) => {
        setSubmitLoading(false);
        console.log(error);
      });
    } else {
      fetchAdminAddTask(jwtToken, taskPayload).then((res) => {
        setSubmitLoading(false);
        if (res.status === "1") {
          message.success(res.message);
          setAddTaskForm({ title: '', comments: '', member: [], task_id: "", need_role: "1", tags: [] });
          setAddTaskDrawerVisible(false);
          adminTask()
        } else if (res.status === "-1") {
          navigate('/logout');
        } else {
          message.error(res.message);
        }
      }).catch((error: any) => {
        setSubmitLoading(false);
        console.log(error);
      });

    }
  };
  const EditTaskHandle = (task: any) => {
    const selectedTask = tasks.find(
      (item: any) => item.task_id === task.task_id
    );

    if (selectedTask) {
      setAddTaskForm({
        title: selectedTask.title,
        comments: selectedTask.comments,
        member: selectedTask.assigned_to.map((item: any) => item.assigned_to),
        task_id: selectedTask.task_id,
        need_role: selectedTask.need_role,
        tags: selectedTask.roles ? normalizeRoles(selectedTask.roles) : []
      });
      console.log({
        title: selectedTask.title,
        comments: selectedTask.comments,
        member: selectedTask.assigned_to.map((item: any) => item.assigned_to),
        task_id: selectedTask.task_id,
        need_role: selectedTask.need_role,
        tags: selectedTask.roles ? selectedTask.roles : []
      })
    }
    setAddTaskDrawerVisible(true);
    setIsEditing(true);
  }

  return (
    <div className="tasklist-page" style={{ padding: '20px', maxWidth: '760px', margin: '0 auto' }}>
      {/* {isManage && <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <Select
          defaultValue="all"
          style={{ width: 60 }}
          dropdownStyle={{ minWidth: 120 }}
        >
          <Option value="all">All</Option>
          <Option value="completed">Completed</Option>
          <Option value="in-progress">In Progress</Option>
        </Select>
        <Input placeholder="Search tasks" style={{ flex: 1 }} />
        <Button type="primary">Search</Button>
      </div>} */}
      {/* Manage mode: date range is controlled by Manage header (Only active) */}

      {!isManage && (
        <div
          style={{
            maxWidth: '560px',
            width: 'calc(100vw - 32px)',
            textAlign: 'center',
            display: 'flex',
            justifyContent: isMobile ? 'space-between' : 'center',
            gap: '10px',
            position: 'fixed',
            left: '50%',
            bottom: '20px',
            transform: 'translateX(-50%)',
            zIndex: 120,
            alignItems: 'center',
          }}
        >
          {!isMobile ? (
            <>
              <div
                style={{
                  height: 64,
                  borderRadius: 60,
                  background: '#ffffff',
                  border: '1px solid rgba(223, 223, 219, 0.90)',
                  boxShadow: '0 9px 20px 0 rgba(14, 31, 39, 0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '0 18px',
                  minWidth: 0,
                }}
              >
                <span style={{ fontSize: 20, fontWeight: 500, color: '#0E1F27', whiteSpace: 'nowrap' }}>
                  My mood
                </span>
                <div style={{ display: 'flex', gap: 10, marginLeft: 6 }}>
                  {(() => {
                    const allMoodIcons = MOOD_ICONS;

                    const hasChosenMood =
                      typeof selfRatingValue === 'number' &&
                      selfRatingValue >= 1 &&
                      selfRatingValue <= allMoodIcons.length;

                    const iconsToRender = hasChosenMood ? [allMoodIcons[selfRatingValue - 1]] : allMoodIcons;

                    return iconsToRender.map((icon, idx) => {
                      const moodValue = hasChosenMood ? (selfRatingValue as number) : idx + 1;

                      return (
                        <span
                          key={`${moodValue}`}
                          style={{
                            position: 'relative',
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: hasChosenMood ? 'default' : 'pointer',
                            color: '#0E1F27',
                          }}
                          aria-label="Mood"
                          role="button"
                          tabIndex={0}
                          onMouseEnter={(e) => {
                            if (hasChosenMood) return;
                            setDesktopMoodHover(moodValue);
                            const paths = e.currentTarget.querySelectorAll('svg path');
                            paths.forEach((p) => p.setAttribute('fill', '#1EAEFF'));
                          }}
                          onMouseLeave={(e) => {
                            setDesktopMoodHover(undefined);
                            const paths = e.currentTarget.querySelectorAll('svg path');
                            paths.forEach((p) => p.setAttribute('fill', 'currentColor'));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                            }
                          }}
                          onClick={async () => {
                            if (hasChosenMood) return;
                            if (isEvaluated) {
                              message.warning('Cannot modify mood after it has been submitted');
                              return;
                            }
                            if (selfRatingSubmitting) return;
                            setDesktopMoodHover(undefined);
                            await submitMoodRating(moodValue);
                          }}
                        >
                          {!hasChosenMood &&
                            desktopMoodHover === moodValue &&
                            moodValue >= 1 &&
                            moodValue <= MOOD_SELF_EVAL_COPY.length && (
                              <div
                                style={{
                                  position: 'absolute',
                                  left: '50%',
                                  bottom: 'calc(100% + 14px)',
                                  transform: 'translateX(-50%)',
                                  background: '#0E1F27',
                                  color: '#fff',
                                  borderRadius: 22,
                                  boxShadow: '0 8px 20px rgba(14, 31, 39, 0.24)',
                                  padding: '16px 20px',
                                  width: 400,
                                  maxWidth: 'min(400px, calc(100vw - 48px))',
                                  zIndex: 10,
                                  pointerEvents: 'none',
                                  textAlign: 'left',
                                }}
                              >
                                <div
                                  style={{
                                    position: 'absolute',
                                    left: '50%',
                                    bottom: -7,
                                    transform: 'translateX(-50%) rotate(45deg)',
                                    width: 14,
                                    height: 14,
                                    background: '#0E1F27',
                                  }}
                                />
                                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6, lineHeight: 1.2 }}>
                                  {MOOD_SELF_EVAL_COPY[moodValue - 1].title}
                                </div>
                                <div style={{ fontSize: 13, lineHeight: 1.45, color: 'rgba(255, 255, 255, 0.92)' }}>
                                  {MOOD_SELF_EVAL_COPY[moodValue - 1].description}
                                </div>
                              </div>
                            )}
                          {icon}
                        </span>
                      );
                    });
                  })()}
                </div>
              </div>

              <Button
                size="large"
                style={{
                  height: 64,
                  borderRadius: 60,
                  background:
                    'radial-gradient(76.89% 119.16% at 50% -19.12%, #102D3D 0%, #0E1F27 100%), #0E1F27',
                  boxShadow: '0 9px 20px 0 rgba(14, 31, 39, 0.25)',
                  border: 'none',
                  color: '#fff',
                  fontSize: 20,
                  fontWeight: 500,
                  padding: '0 28px',
                }}
                icon={
                  <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M18.3113 4.62821L14.1216 0.439461C13.9823 0.300137 13.8169 0.189617 13.6349 0.114213C13.4529 0.03881 13.2578 0 13.0608 0C12.8638 0 12.6687 0.03881 12.4867 0.114213C12.3047 0.189617 12.1393 0.300137 12 0.439461L0.439695 11.9998C0.299801 12.1386 0.188889 12.3038 0.113407 12.4858C0.0379245 12.6678 -0.000621974 12.863 7.58901e-06 13.0601V17.2498C7.58901e-06 17.6476 0.158043 18.0291 0.439347 18.3104C0.720652 18.5917 1.10218 18.7498 1.50001 18.7498H5.68969C5.88675 18.7504 6.08196 18.7119 6.26399 18.6364C6.44602 18.5609 6.61122 18.45 6.75001 18.3101L14.5941 10.467L14.9203 11.7701L11.4703 15.2191C11.3296 15.3598 11.2505 15.5505 11.2504 15.7494C11.2503 15.9484 11.3292 16.1392 11.4699 16.2799C11.6105 16.4207 11.8012 16.4998 12.0001 16.4999C12.1991 16.4999 12.3899 16.421 12.5306 16.2804L16.2806 12.5304C16.3724 12.4388 16.4388 12.3251 16.4734 12.2002C16.5081 12.0754 16.5098 11.9436 16.4784 11.8179L15.8316 9.22946L18.3113 6.74977C18.4506 6.61048 18.5611 6.44511 18.6365 6.2631C18.7119 6.08109 18.7507 5.886 18.7507 5.68899C18.7507 5.49198 18.7119 5.2969 18.6365 5.11489C18.5611 4.93288 18.4506 4.7675 18.3113 4.62821ZM15 7.93946L10.8113 3.74977L13.0613 1.49977L17.25 5.68946L15 7.93946Z"
                      fill="white"
                    />
                  </svg>
                }
                onClick={() => {
                  setVisible(true);
                }}
              >
                Add entry
              </Button>
            </>
          ) : (
            <>
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (isEvaluated && selfRatingValue) return;
                  if (selfRatingSubmitting) return;
                  setMobileMoodDraft(undefined);
                  setMobileMoodModalVisible(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!(isEvaluated && selfRatingValue) && !selfRatingSubmitting) {
                      setMobileMoodDraft(undefined);
                      setMobileMoodModalVisible(true);
                    }
                  }
                }}
                style={{
                  minWidth: 0,
                  height: 52,
                  borderRadius: 60,
                  background: '#ffffff',
                  border: '1px solid rgba(223, 223, 219, 0.90)',
                  boxShadow: '0 9px 20px 0 rgba(14, 31, 39, 0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  padding: '0 14px',
                  cursor: isEvaluated && selfRatingValue ? 'default' : 'pointer',
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 500, color: '#0E1F27', whiteSpace: 'nowrap' }}>My mood</span>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#0E1F27',
                    flexShrink: 0,
                  }}
                >
                  {typeof selfRatingValue === 'number' &&
                    selfRatingValue >= 1 &&
                    selfRatingValue <= MOOD_ICONS.length ? (
                    MOOD_ICONS[selfRatingValue - 1]
                  ) : (
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: '#D8D8D3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: 15,
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      ?
                    </div>
                  )}
                </div>
              </div>
              <Button
                size="large"
                style={{
                  height: isMobile ? 52 : 64,
                  borderRadius: 60,
                  background:
                    'radial-gradient(76.89% 119.16% at 50% -19.12%, #102D3D 0%, #0E1F27 100%), #0E1F27',
                  boxShadow: '0 9px 20px 0 rgba(14, 31, 39, 0.25)',
                  border: 'none',
                  color: '#fff',
                  fontSize: isMobile ? 16 : 20,
                  fontWeight: 500,
                  padding: isMobile ? '16px' : '20px 24px',
                }}
                icon={
                  <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M18.3113 4.62821L14.1216 0.439461C13.9823 0.300137 13.8169 0.189617 13.6349 0.114213C13.4529 0.03881 13.2578 0 13.0608 0C12.8638 0 12.6687 0.03881 12.4867 0.114213C12.3047 0.189617 12.1393 0.300137 12 0.439461L0.439695 11.9998C0.299801 12.1386 0.188889 12.3038 0.113407 12.4858C0.0379245 12.6678 -0.000621974 12.863 7.58901e-06 13.0601V17.2498C7.58901e-06 17.6476 0.158043 18.0291 0.439347 18.3104C0.720652 18.5917 1.10218 18.7498 1.50001 18.7498H5.68969C5.88675 18.7504 6.08196 18.7119 6.26399 18.6364C6.44602 18.5609 6.61122 18.45 6.75001 18.3101L14.5941 10.467L14.9203 11.7701L11.4703 15.2191C11.3296 15.3598 11.2505 15.5505 11.2504 15.7494C11.2503 15.9484 11.3292 16.1392 11.4699 16.2799C11.6105 16.4207 11.8012 16.4998 12.0001 16.4999C12.1991 16.4999 12.3899 16.421 12.5306 16.2804L16.2806 12.5304C16.3724 12.4388 16.4388 12.3251 16.4734 12.2002C16.5081 12.0754 16.5098 11.9436 16.4784 11.8179L15.8316 9.22946L18.3113 6.74977C18.4506 6.61048 18.5611 6.44511 18.6365 6.2631C18.7119 6.08109 18.7507 5.886 18.7507 5.68899C18.7507 5.49198 18.7119 5.2969 18.6365 5.11489C18.5611 4.93288 18.4506 4.7675 18.3113 4.62821ZM15 7.93946L10.8113 3.74977L13.0613 1.49977L17.25 5.68946L15 7.93946Z"
                      fill="white"
                    />
                  </svg>
                }
                onClick={() => {
                  setVisible(true);
                }}
              >
                Add entry
              </Button>
            </>
          )}
        </div>
      )}
      {!isManage && (
        <Modal
          title={null}
          open={mobileMoodModalVisible && isMobile}
          onCancel={() => {
            setMobileMoodModalVisible(false);
            setMobileMoodDraft(undefined);
          }}
          footer={null}
          centered
          width={400}
          destroyOnClose
          styles={{
            body: { padding: '20px 20px 24px' },
            content: { maxWidth: 'calc(100vw - 32px)' },
            mask: {
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              background: 'rgba(0, 0, 0, 0.45)',
            },
          }}
        >
          <p
            style={{
              margin: '0 0 16px',
              fontSize: 15,
              color: '#0E1F27',
              lineHeight: 1.45,
              fontWeight: 500,
            }}
          >
            How do you feel about your performance today? Select below:
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 6,
              marginBottom: 16,
            }}
          >
            {MOOD_ICONS.map((node, i) => {
              const v = i + 1;
              const active = mobileMoodDraft === v;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setMobileMoodDraft(v)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 4,
                    cursor: 'pointer',
                    color: active ? '#1EAEFF' : '#0E1F27',
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  aria-label={`Mood ${v}`}
                >
                  {node}
                </button>
              );
            })}
          </div>
          {mobileMoodDraft != null &&
            mobileMoodDraft >= 1 &&
            mobileMoodDraft <= MOOD_SELF_EVAL_COPY.length && (
              <>
                <div
                  style={{
                    background: '#0E1F27',
                    borderRadius: 12,
                    padding: '14px 16px',
                    marginBottom: 16,
                  }}
                >
                  <div style={{ color: '#fff', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
                    {MOOD_SELF_EVAL_COPY[mobileMoodDraft - 1].title}
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.88)', fontSize: 13, lineHeight: 1.45 }}>
                    {MOOD_SELF_EVAL_COPY[mobileMoodDraft - 1].description}
                  </div>
                </div>
                <Button
                  block
                  size="large"
                  loading={selfRatingSubmitting}
                  onClick={async () => {
                    if (!mobileMoodDraft) return;
                    const ok = await submitMoodRating(mobileMoodDraft);
                    if (ok) {
                      setMobileMoodModalVisible(false);
                      setMobileMoodDraft(undefined);
                    }
                  }}
                  style={{
                    height: 48,
                    borderRadius: 60,
                    background:
                      'radial-gradient(76.89% 119.16% at 50% -19.12%, #102D3D 0%, #0E1F27 100%), #0E1F27',
                    boxShadow: '0 9px 20px 0 rgba(14, 31, 39, 0.25)',
                    border: 'none',
                    color: '#fff',
                    fontWeight: 600,
                  }}
                >
                  Confirm
                </Button>
              </>
            )}
        </Modal>
      )}
      {loading ? (
        <Spin
          tip="Loading..."
          indicator={<LoadingOutlined style={{ fontSize: 24, color: '#171717' }} spin />}
          style={{ display: 'block', margin: '100px auto' }}
        />
      ) :
        <List
          split={false}
          dataSource={tasks.filter((t: any) => {
            const selectedOk =
              !selectedTaskId ||
              String(t?.task_id ?? '') === String(selectedTaskId);
            const activeOk =
              !onlyActive ||
              (t?.status === undefined || t?.status === null ? true : t?.status !== 'Deleted');

            const normalizedActiveDate = activeDate ? String(activeDate).slice(0, 10) : undefined;
            const normalizedRangeStart = activeDateRange?.start ? String(activeDateRange.start).slice(0, 10) : undefined;
            const normalizedRangeEnd = activeDateRange?.end ? String(activeDateRange.end).slice(0, 10) : undefined;
            const taskDateRaw = t?.take_date ?? t?.search_date ?? '';
            const normalizedTaskDate = taskDateRaw ? String(taskDateRaw).slice(0, 10) : '';
            // Backend may not provide a date field for manage tasks; don't hide everything in that case.
            const hasTaskDate = Boolean(normalizedTaskDate);
            let dateOk = true;

            if (normalizedRangeStart && normalizedRangeEnd) {
              // Inclusive range filter (normalize order just in case)
              const minDate = normalizedRangeStart <= normalizedRangeEnd ? normalizedRangeStart : normalizedRangeEnd;
              const maxDate = normalizedRangeStart <= normalizedRangeEnd ? normalizedRangeEnd : normalizedRangeStart;
              dateOk = !hasTaskDate || (normalizedTaskDate >= minDate && normalizedTaskDate <= maxDate);
            } else if (normalizedActiveDate) {
              dateOk = !hasTaskDate || normalizedTaskDate === normalizedActiveDate;
            }

            return selectedOk && activeOk && dateOk;
          })}
          renderItem={(task: any) => {
            const projectLine =
              task.title && String(task.title).trim()
                ? String(task.title).charAt(0).toUpperCase() + String(task.title).slice(1)
                : '';
            const hasComments = !!(task.comments && String(task.comments).trim());
            const mainText = hasComments
              ? String(task.comments)
              : projectLine || '—';

            // Manage端任务列表项交给专用组件渲染，避免和员工端展示逻辑混在一起
            if (isManage) {
              return (
                <ManageTaskListItem
                  task={task}
                  onEdit={EditTaskHandle}
                  onDelete={(taskLogId) => handleDeleteTask(taskLogId)}
                />
              );
            }

            const taskMenuItems = [
              {
                key: 'delete',
                label: 'Delete',
                danger: true,
                onClick: () => handleDeleteTask(task.task_log_id),
              },
            ];

            // const taskMenuItems = isManage
            //   ? [
            //       {
            //         key: 'edit',
            //         label: 'Edit',
            //         icon: <EditOutlined />,
            //         onClick: () => EditTaskHandle(task),
            //       },
            //       {
            //         key: 'delete',
            //         label: 'Delete',
            //         danger: true,
            //         onClick: () => handleDeleteTask(task.task_log_id),
            //       },
            //     ]
            //   : [
            //       {
            //         key: 'delete',
            //         label: 'Delete',
            //         danger: true,
            //         onClick: () => handleDeleteTask(task.task_log_id),
            //       },
            //     ];

            return (
              <div
                key={task.task_log_id}
                style={{
                  marginBottom: 16,
                  width: '100%',
                  background: '#fff',
                  borderRadius: 20,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
                  padding: '20px 20px 18px',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: hasComments && projectLine ? 10 : 8,
                  }}
                >
                  <Tooltip title={task.role}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        maxWidth: '58%',
                        minWidth: 0,
                        border: '1px solid #E1E1E1',
                        borderRadius: 16,
                        background: '#FBFBF7',
                        padding: '4px 12px',
                        fontSize: 13,
                        color: '#0E1F27',
                        lineHeight: 1.3,
                      }}
                    >
                      <ProjectOutlined style={{ fontSize: 14, color: '#8A8A86', flexShrink: 0 }} />
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {task.role || '—'}
                      </span>
                    </span>
                  </Tooltip>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexShrink: 0,
                    }}
                  >
                    <ClockCircleOutlined style={{ color: '#8A8A86', fontSize: 16 }} />
                    <span style={{ fontSize: 14, color: '#0E1F27', fontWeight: 500 }}>
                      {formatEstimatedHours(task.estimated_hrs)}
                    </span>
                    <Dropdown menu={{ items: taskMenuItems }} trigger={['click']} placement="bottomRight">
                      <Button
                        type="text"
                        size="small"
                        icon={<MoreOutlined style={{ fontSize: 18, color: '#8A8A86' }} />}
                        style={{ padding: '4px 6px', minWidth: 32 }}
                        aria-label="Task actions"
                      />
                    </Dropdown>
                  </div>
                </div>

                {hasComments && projectLine ? (
                  <div
                    style={{
                      fontSize: 13,
                      color: '#8A8A86',
                      marginBottom: 8,
                      lineHeight: 1.45,
                    }}
                  >
                    {projectLine}
                  </div>
                ) : null}

                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#171717',
                    lineHeight: 1.45,
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {mainText}
                </div>

                {isManage && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 14,
                      paddingTop: 14,
                      borderTop: '1px solid #F0F0ED',
                    }}
                  >
                    <div>
                      <Text type="secondary">DKJ ID: </Text>
                      <Text>{task.task_id}</Text>
                    </div>
                  </div>
                )}
              </div>
            );
          }}
          locale={{
            emptyText: (
              <div style={{ marginTop: 80 }}>
                <Empty
                  image={<LogsEmptyIcon />}
                  description={
                    <div style={{ marginTop: 8 }}>
                      <div style={{ color: '#171717', fontSize: 32, fontWeight: 400 }}>
                        No logs yet
                      </div>
                      <div style={{ color: '#545454', fontSize: 14, lineHeight: '20px', marginTop: 20 }}>
                        Start by clicking “Add entry” to log a task.
                      </div>
                    </div>
                  }
                />
              </div>
            ),
          }}
        />}
      {/* {!isManage && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          right: '20px',
          padding: '20px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          border: '1px solid #e8e8e8',
          boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
          zIndex: 100
        }}>
          <div style={{ marginBottom: '12px' }}>
            <Text strong style={{ fontSize: '16px' }}>
              Status: {isEvaluated ? 'Submitted' : 'Pending Submission'}
            </Text>
          </div>
          <div>
            <Text style={{ fontSize: '14px', color: '#666' }}>
              End of Day Self-Rating: {
                isEvaluated && selfRatingValue ? (
                  <>
                    {selfRatingValue} ({getRatingDescription(selfRatingValue)})
                  </>
                ) : (
                  'Not Rated'
                )
              }
            </Text>
            {!isEvaluated && (
              <div style={{ marginTop: '8px' }}>
                <Text style={{ fontSize: '14px', color: '#ff4d4f' }}>
                  Your day is not submitted yet. Please complete Self-Rating to finalize.
                </Text>
              </div>
            )}
          </div>
        </div>
      )} */}
      {!isManage && <div style={{ height: 150 }}></div>}
      {isManage && <div style={{ height: 140 }}></div>}

      {isManage && (
        <div className="manage-bottom-actions">
          <Button
            className="manage-bottom-btn manage-bottom-download"
            onClick={handleDownload}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <span>Download</span>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M18 11.25V17.25C18 17.4489 17.921 17.6397 17.7803 17.7803C17.6397 17.921 17.4489 18 17.25 18H0.75C0.551088 18 0.360322 17.921 0.21967 17.7803C0.0790178 17.6397 0 17.4489 0 17.25V11.25C0 11.0511 0.0790178 10.8603 0.21967 10.7197C0.360322 10.579 0.551088 10.5 0.75 10.5C0.948912 10.5 1.13968 10.579 1.28033 10.7197C1.42098 10.8603 1.5 11.0511 1.5 11.25V16.5H16.5V11.25C16.5 11.0511 16.579 10.8603 16.7197 10.7197C16.8603 10.579 17.0511 10.5 17.25 10.5C17.4489 10.5 17.6397 10.579 17.7803 10.7197C17.921 10.8603 18 11.0511 18 11.25ZM8.46937 11.7806C8.53903 11.8504 8.62175 11.9057 8.7128 11.9434C8.80384 11.9812 8.90144 12.0006 9 12.0006C9.09856 12.0006 9.19616 11.9812 9.2872 11.9434C9.37825 11.9057 9.46097 11.8504 9.53063 11.7806L13.2806 8.03063C13.3503 7.96094 13.4056 7.87822 13.4433 7.78717C13.481 7.69613 13.5004 7.59855 13.5004 7.5C13.5004 7.40145 13.481 7.30387 13.4433 7.21283C13.4056 7.12178 13.3503 7.03906 13.2806 6.96937C13.2109 6.89969 13.1282 6.84442 13.0372 6.8067C12.9461 6.76899 12.8485 6.74958 12.75 6.74958C12.6515 6.74958 12.5539 6.76899 12.4628 6.8067C12.3718 6.84442 12.2891 6.89969 12.2194 6.96937L9.75 9.43969V0.75C9.75 0.551088 9.67098 0.360322 9.53033 0.21967C9.38968 0.0790176 9.19891 0 9 0C8.80109 0 8.61032 0.0790176 8.46967 0.21967C8.32902 0.360322 8.25 0.551088 8.25 0.75V9.43969L5.78063 6.96937C5.63989 6.82864 5.44902 6.74958 5.25 6.74958C5.05098 6.74958 4.86011 6.82864 4.71937 6.96937C4.57864 7.11011 4.49958 7.30098 4.49958 7.5C4.49958 7.69902 4.57864 7.88989 4.71937 8.03063L8.46937 11.7806Z"
                  fill="black"
                />
              </svg>
            </span>
          </Button>

          <Button
            className="manage-bottom-btn manage-bottom-new"
            type="primary"
            onClick={() => { setAddTaskDrawerVisible(true); setIsEditing(false) }}
          >
            <span className="manage-bottom-new-content">
              <span>New project</span>
                <span className="manage-bottom-plus">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.75 0C7.82164 0 5.93657 0.571828 4.33319 1.64317C2.72982 2.71451 1.48013 4.23726 0.742179 6.01884C0.00422452 7.80042 -0.188858 9.76082 0.187348 11.6521C0.563554 13.5434 1.49215 15.2807 2.85571 16.6443C4.21928 18.0079 5.95656 18.9365 7.84787 19.3127C9.73919 19.6889 11.6996 19.4958 13.4812 18.7578C15.2627 18.0199 16.7855 16.7702 17.8568 15.1668C18.9282 13.5634 19.5 11.6784 19.5 9.75C19.4968 7.16513 18.4685 4.68705 16.6407 2.85927C14.813 1.03149 12.3349 0.00322505 9.75 0ZM13.5 10.5H10.5V13.5C10.5 13.6989 10.421 13.8897 10.2803 14.0303C10.1397 14.171 9.94892 14.25 9.75 14.25C9.55109 14.25 9.36033 14.171 9.21967 14.0303C9.07902 13.8897 9 13.6989 9 13.5V10.5H6C5.80109 10.5 5.61033 10.421 5.46967 10.2803C5.32902 10.1397 5.25 9.94891 5.25 9.75C5.25 9.55109 5.32902 9.36032 5.46967 9.21967C5.61033 9.07902 5.80109 9 6 9H9V6C9 5.80109 9.07902 5.61032 9.21967 5.46967C9.36033 5.32902 9.55109 5.25 9.75 5.25C9.94892 5.25 10.1397 5.32902 10.2803 5.46967C10.421 5.61032 10.5 5.80109 10.5 6V9H13.5C13.6989 9 13.8897 9.07902 14.0303 9.21967C14.171 9.36032 14.25 9.55109 14.25 9.75C14.25 9.94891 14.171 10.1397 14.0303 10.2803C13.8897 10.421 13.6989 10.5 13.5 10.5Z" fill="white"/>
                      </svg>
                </span>
            </span>
          </Button>
        </div>
      )}
      {auth === "dkj-admin" && !isManage && <Button
        type="primary"
        shape="circle"
        size='large'
        icon={<SwapOutlined />}
        style={{
          position: 'fixed',
          bottom: '100px',
          right: '20px',
          fontSize: '24px',
          background: '#0E1F27',
          borderColor: '#0E1F27',
          color: '#fff',
        }}
        onClick={() => {
          navigate('/manage')
        }}
      />}
      
      {auth === "dkj-admin" && isManage && (<>
        <Button
          size='large'
          type="primary"
          shape="circle"
          icon={<SwapOutlined />}
          style={{
            position: 'fixed',
            bottom: '100px',
            right: '20px',
            fontSize: '24px',
            background: '#0E1F27',
            borderColor: '#0E1F27',
            color: '#fff',
          }}
          onClick={() => {
            navigate('/employee')
          }}
        />
      </>)}

      {/* Custom Add entry overlay */}
      {visible && (
        <div
          onClick={() => { if (!submitLoading) setVisible(false); }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.35)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'stretch',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100vw',
              height: '94vh',
              background: '#FBFBF7',
              borderTopLeftRadius: isMobile ? 12 : 16,
              borderTopRightRadius: isMobile ? 12 : 16,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <button
              type="button"
              aria-label="Close"
              disabled={submitLoading}
              onClick={() => { if (!submitLoading) setVisible(false); }}
              style={{
                position: 'absolute',
                right: isMobile ? 14 : 20,
                top: isMobile ? 14 : 20,
                width: 36,
                height: 36,
                border: '1px solid #ECECE7',
                borderRadius: '50%',
                background: '#F5F5F2',
                color: '#9D9D98',
                fontSize: 20,
                lineHeight: '34px',
                textAlign: 'center',
                padding: 0,
                cursor: submitLoading ? 'not-allowed' : 'pointer',
                zIndex: 5,
              }}
            >
              ×
            </button>
            <div
              style={{
                flex: 1,
                padding: isMobile ? '14px 14px 12px' : '22px 28px 16px',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <div
                style={{
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: isMobile ? '100%' : 560,
                    margin: isMobile ? '0' : '0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ fontSize: isMobile ? 32 : 40, lineHeight: 1, fontWeight: 500, color: '#171717' }}>Add Entry</div>
                  <div
                    style={{
                      padding: '10px 16px',
                      borderRadius: 12,
                      border: '1px solid #E1E1E1',
                      background: '#FBFBF7',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 16,
                      color: '#0E1F27',
                    }}
                  >
                    {headerSelectedDate ? moment(headerSelectedDate).format('D MMM YYYY') : moment().format('D MMM YYYY')}
                    <svg width="20" height="20" viewBox="0 0 17 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11.6334 0.799988V4.13332M4.96672 0.799988V4.13332M0.800049 7.46665H15.8M2.46672 2.46665H14.1334C15.0539 2.46665 15.8 3.21285 15.8 4.13332V15.8C15.8 16.7205 15.0539 17.4667 14.1334 17.4667H2.46672C1.54624 17.4667 0.800049 16.7205 0.800049 15.8V4.13332C0.800049 3.21285 1.54624 2.46665 2.46672 2.46665Z" stroke="#8c8c8a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', paddingRight: 2, marginTop: '20px' }}>
                <div style={{ width: isMobile ? '100%' : 560, margin: isMobile ? '0' : '0 auto' }}>
                  <div style={{ marginBottom: 24 }}>
                    <Input.TextArea
                      placeholder="Task description*"
                      value={newTask.comments}
                      onChange={(e) => setNewTask({ ...newTask, comments: e.target.value })}
                      rows={4}
                      style={{ borderRadius: 10, height: 108, resize: 'none', fontSize: 16 }}
                    />
                  </div>

                  <div style={{ marginBottom: 24 }}>
                    <Select
                      size="large"
                      placeholder="Select project*"
                      value={newTask.title || undefined}
                      onChange={(e) => {
                        const selectedTask = unclaimedTaskList.find((task) => task.task_id === e);
                        setNewTask({
                          ...newTask,
                          title: e,
                          role: '',
                          roleDescription: ''
                        });
                        setRoles(selectedTask?.roles ? normalizeRoles(selectedTask?.roles) : []);
                      }}
                      style={{ width: '100%', height: 42, fontSize: 16 }}
                      dropdownStyle={{ zIndex: 1500 }}
                      getPopupContainer={() => document.body}
                    >
                      {unclaimedTaskList.map((task, index) => (
                        <Option key={index} value={task.task_id}>
                          {task.title}
                        </Option>
                      ))}
                    </Select>
                  </div>

                  <div style={{ marginBottom: 24 }}>
                    <Select
                      size="large"
                      placeholder="Select function*"
                      value={newTask.role || undefined}
                      onChange={(value) => {
                        const selectFun = roles.find((item) => item.name === value);
                        setNewTask({ ...newTask, role: value, roleDescription: selectFun ? selectFun.description : "" });
                      }}
                      style={{ width: '100%', height: 42, fontSize: 16 }}
                      dropdownStyle={{ zIndex: 1500 }}
                      getPopupContainer={() => document.body}
                    >
                      {roles && roles.length > 0 ? (
                        roles.map((item: any) => (
                          <Option key={item.name} value={item.name}>
                            {item.name}
                          </Option>
                        ))
                      ) : (
                        <>
                          <Option value="PM">PM</Option>
                          <Option value="BA">BA</Option>
                          <Option value="DEV">DEV</Option>
                          <Option value="QA">QA</Option>
                          <Option value="Meeting">Meeting</Option>
                          <Option value="Others">Others</Option>
                        </>
                      )}
                    </Select>
                  </div>

                  <div style={{ marginBottom: 24 }}>
                    <Select
                      size="large"
                      placeholder="0 h    00 min"
                      value={newTask.hours || undefined}
                      onChange={(value) => setNewTask({ ...newTask, hours: value })}
                      style={{ width: isMobile ? '100%' : 160, height: 42, fontSize: 16 }}
                      options={HOUR_OPTIONS}
                      dropdownStyle={{
                        zIndex: 1500,
                        borderRadius: 10,
                        overflow: 'hidden',
                      }}
                      getPopupContainer={() => document.body}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 18,
                  marginTop: 24,
                }}
              >
                <Button
                  type="text"
                  disabled={submitLoading}
                  onClick={() => setVisible(false)}
                  style={{ color: '#171717', fontSize: 16, fontWeight: 500, padding: 0 }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleAddTask}
                  loading={submitLoading}
                  size="large"
                  icon={submitLoading ? <Spin size="small" indicator={<LoadingOutlined style={{ color: '#171717' }} spin />} /> : null}
                  style={{
                    minWidth: isMobile ? 168 : 186,
                    height: 42,
                    borderRadius: 999,
                    border: 'none',
                    background:
                      'radial-gradient(76.89% 119.16% at 50% -19.12%, #102D3D 0%, #0E1F27 100%), #0E1F27',
                    boxShadow: '0 9px 20px 0 rgba(14, 31, 39, 0.25)',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span>Save time entry</span>
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.1936 7.12754C18.1936 6.89268 18.2632 6.66309 18.3937 6.46781C18.5242 6.27253 18.7097 6.12033 18.9266 6.03045C19.1436 5.94058 19.3824 5.91706 19.6127 5.96288C19.8431 6.0087 20.0547 6.12179 20.2207 6.28787C20.3868 6.45394 20.4999 6.66552 20.5457 6.89587C20.5915 7.12622 20.568 7.36498 20.4781 7.58196C20.3883 7.79895 20.2361 7.98441 20.0408 8.11489C19.8455 8.24537 19.6159 8.31501 19.3811 8.31501C19.0661 8.31501 18.7641 8.1899 18.5414 7.96721C18.3187 7.74451 18.1936 7.44247 18.1936 7.12754ZM17.0061 4.75259C17.241 4.75259 17.4706 4.68294 17.6659 4.55246C17.8611 4.42198 18.0133 4.23652 18.1032 4.01954C18.1931 3.80256 18.2166 3.5638 18.1708 3.33345C18.125 3.1031 18.0119 2.89151 17.8458 2.72544C17.6797 2.55937 17.4681 2.44627 17.2378 2.40046C17.0075 2.35464 16.7687 2.37815 16.5517 2.46803C16.3347 2.55791 16.1493 2.71011 16.0188 2.90539C15.8883 3.10067 15.8187 3.33025 15.8187 3.56511C15.8187 3.88005 15.9438 4.18209 16.1665 4.40479C16.3892 4.62748 16.6912 4.75259 17.0061 4.75259ZM19.8422 10.2941C19.633 10.2769 19.4256 10.3434 19.2655 10.479C19.1053 10.6147 19.0056 10.8084 18.9882 11.0175C18.8465 12.6725 18.2351 14.2526 17.2259 15.5719C16.2167 16.8912 14.8516 17.8948 13.2914 18.4646C11.7312 19.0345 10.0406 19.1468 8.41871 18.7884C6.79679 18.43 5.31097 17.6159 4.13607 16.4417C2.96117 15.2675 2.14607 13.7822 1.78668 12.1605C1.42729 10.5388 1.53855 8.84816 2.10738 7.28754C2.67621 5.72692 3.67895 4.36124 4.9976 3.35118C6.31625 2.34112 7.89596 1.7287 9.55085 1.58599C9.65481 1.57741 9.75606 1.54844 9.84883 1.50073C9.94159 1.45303 10.0241 1.38751 10.0915 1.30794C10.1589 1.22836 10.2101 1.13628 10.2419 1.03695C10.2738 0.937621 10.2858 0.832989 10.2772 0.729028C10.2686 0.625067 10.2396 0.523814 10.1919 0.431048C10.1442 0.338283 10.0787 0.255823 9.99914 0.188377C9.91956 0.12093 9.82748 0.069817 9.72815 0.0379568C9.62882 0.00609656 9.52419 -0.0058872 9.42023 0.00268958C7.4649 0.170881 5.59828 0.894031 4.03998 2.08706C2.48168 3.28009 1.29652 4.89337 0.623939 6.73709C-0.0486393 8.58081 -0.180658 10.5783 0.243415 12.4945C0.667488 14.4107 1.63001 16.1659 3.01774 17.5537C4.40547 18.9414 6.16068 19.9039 8.07687 20.328C9.99305 20.7521 11.9905 20.6201 13.8342 19.9475C15.6779 19.2749 17.2912 18.0897 18.4842 16.5314C19.6772 14.9731 20.4003 13.1064 20.5685 11.1511C20.5775 11.0471 20.5657 10.9423 20.534 10.8428C20.5023 10.7433 20.4512 10.6511 20.3837 10.5715C20.3162 10.4918 20.2336 10.4263 20.1406 10.3787C20.0477 10.3311 19.9463 10.3024 19.8422 10.2941ZM10.2772 3.16929C11.6863 3.16929 13.0638 3.58715 14.2355 4.37004C15.4072 5.15293 16.3204 6.26568 16.8596 7.56757C17.3989 8.86947 17.54 10.302 17.2651 11.6841C16.9901 13.0662 16.3116 14.3357 15.3152 15.3322C14.3187 16.3286 13.0492 17.0072 11.6672 17.2821C10.2851 17.557 8.85253 17.4159 7.55065 16.8766C6.24877 16.3374 5.13603 15.4242 4.35315 14.2525C3.57027 13.0808 3.15241 11.7033 3.15241 10.2941C3.15451 8.40515 3.90582 6.59414 5.24152 5.25842C6.57722 3.92271 8.38822 3.17138 10.2772 3.16929ZM9.48554 10.2941C9.48554 10.5041 9.56895 10.7055 9.71741 10.8539C9.86587 11.0024 10.0672 11.0858 10.2772 11.0858H15.027C15.237 11.0858 15.4383 11.0024 15.5868 10.8539C15.7353 10.7055 15.8187 10.5041 15.8187 10.2941C15.8187 10.0842 15.7353 9.88282 15.5868 9.73435C15.4383 9.58589 15.237 9.50249 15.027 9.50249H11.0688V5.54424C11.0688 5.33428 10.9854 5.13292 10.837 4.98446C10.6885 4.83599 10.4871 4.75259 10.2772 4.75259C10.0672 4.75259 9.86587 4.83599 9.71741 4.98446C9.56895 5.13292 9.48554 5.33428 9.48554 5.54424V10.2941ZM13.4437 2.37764C13.6786 2.37764 13.9082 2.30799 14.1035 2.17751C14.2987 2.04703 14.4509 1.86157 14.5408 1.64459C14.6307 1.42761 14.6542 1.18885 14.6084 0.958499C14.5626 0.728152 14.4495 0.516564 14.2834 0.350493C14.1173 0.184422 13.9058 0.0713256 13.6754 0.0255067C13.4451 -0.0203123 13.2063 0.00320364 12.9893 0.0930808C12.7723 0.182958 12.5869 0.335159 12.4564 0.530439C12.3259 0.725718 12.2563 0.955304 12.2563 1.19016C12.2563 1.5051 12.3814 1.80714 12.6041 2.02984C12.8268 2.25253 13.1288 2.37764 13.4437 2.37764Z" fill="white" />
                    </svg>
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}


      {addTaskDrawerVisible && (
        <div
          onClick={() => {
            if (!submitLoading) {
              setAddTaskDrawerVisible(false);
              setAddTaskForm({ title: '', comments: '', member: [], task_id: '', need_role: '1', tags: [] });
              setInputValue('');
            }
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.35)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'stretch',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100vw',
              height: '94vh',
              background: '#FBFBF7',
              borderTopLeftRadius: isMobile ? 12 : 16,
              borderTopRightRadius: isMobile ? 12 : 16,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <button
              type="button"
              aria-label="Close"
              disabled={submitLoading}
              onClick={() => {
                if (!submitLoading) {
                  setAddTaskDrawerVisible(false);
                  setAddTaskForm({ title: '', comments: '', member: [], task_id: '', need_role: '1', tags: [] });
                  setInputValue('');
                }
              }}
              style={{
                position: 'absolute',
                right: isMobile ? 14 : 20,
                top: isMobile ? 14 : 20,
                width: 36,
                height: 36,
                border: '1px solid #ECECE7',
                borderRadius: '50%',
                background: '#F5F5F2',
                color: '#9D9D98',
                fontSize: 20,
                lineHeight: '34px',
                textAlign: 'center',
                padding: 0,
                cursor: submitLoading ? 'not-allowed' : 'pointer',
                zIndex: 5,
              }}
            >
              ×
            </button>
            <div
              style={{
                flex: 1,
                padding: isMobile ? '14px 14px 12px' : '22px 28px 16px',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <div
                style={{
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    width: isMobile ? '100%' : 560,
                    margin: isMobile ? '0' : '0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobile ? 26 : 32,
                      lineHeight: 1.15,
                      fontWeight: 600,
                      color: '#171717',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {isEditing ? 'Manage project' : 'New project'}
                  </div>
                </div>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', paddingRight: 2, marginTop: 20 }}>
                <div style={{ width: isMobile ? '100%' : 560, margin: isMobile ? '0' : '0 auto' }}>
                  <div style={{ marginBottom: 22 }}>
                    <Input
                      placeholder="Project (product) name*"
                      value={addTaskForm.title}
                      onChange={(e) => setAddTaskForm({ ...addTaskForm, title: e.target.value })}
                      style={{
                        borderRadius: 10,
                        height: 46,
                        fontSize: 16,
                        borderColor: '#E4E7EC',
                        background: '#fff',
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: 22 }}>
                    <Input.TextArea
                      placeholder="Project (product) description*"
                      value={addTaskForm.comments}
                      onChange={(e) => setAddTaskForm({ ...addTaskForm, comments: e.target.value })}
                      rows={6}
                      style={{
                        borderRadius: 10,
                        resize: 'none',
                        fontSize: 16,
                        minHeight: 152,
                        borderColor: '#E4E7EC',
                        background: '#fff',
                        paddingTop: 12,
                        paddingBottom: 12,
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: 22 }}>
                    <Input
                      prefix={<SearchOutlined style={{ color: '#98A2B3', fontSize: 16 }} />}
                      placeholder="Find or create a custom function..."
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onPressEnter={handleInputConfirm}
                      style={{
                        borderRadius: 10,
                        height: 46,
                        fontSize: 16,
                        borderColor: '#E4E7EC',
                        background: '#fff',
                      }}
                    />
                    <div style={{ marginTop: 8, fontSize: 13, color: '#98A2B3' }}>Press Enter to add</div>
                    {addTaskForm.tags.length > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 8,
                          marginTop: 14,
                        }}
                      >
                        {addTaskForm.tags.map((tag: any) => (
                          <Tag
                            key={tag.name}
                            closable
                            closeIcon={
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 17,
                                  height: 17,
                                }}
                              >
                                <svg
                                  width="17"
                                  height="17"
                                  viewBox="0 0 17 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  style={{ display: 'block' }}
                                >
                                  <path
                                    d="M11.0672 6.06719L9.0086 8.125L11.0672 10.1828C11.1253 10.2409 11.1713 10.3098 11.2027 10.3857C11.2342 10.4616 11.2504 10.5429 11.2504 10.625C11.2504 10.7071 11.2342 10.7884 11.2027 10.8643C11.1713 10.9402 11.1253 11.0091 11.0672 11.0672C11.0091 11.1253 10.9402 11.1713 10.8643 11.2027C10.7884 11.2342 10.7071 11.2503 10.625 11.2503C10.5429 11.2503 10.4616 11.2342 10.3857 11.2027C10.3098 11.1713 10.2409 11.1253 10.1828 11.0672L8.125 9.00859L6.06719 11.0672C6.00912 11.1253 5.94018 11.1713 5.86431 11.2027C5.78844 11.2342 5.70713 11.2503 5.625 11.2503C5.54288 11.2503 5.46156 11.2342 5.38569 11.2027C5.30982 11.1713 5.24088 11.1253 5.18282 11.0672C5.12475 11.0091 5.07868 10.9402 5.04726 10.8643C5.01583 10.7884 4.99966 10.7071 4.99966 10.625C4.99966 10.5429 5.01583 10.4616 5.04726 10.3857C5.07868 10.3098 5.12475 10.2409 5.18282 10.1828L7.24141 8.125L5.18282 6.06719C5.06554 5.94991 4.99966 5.79085 4.99966 5.625C4.99966 5.45915 5.06554 5.30009 5.18282 5.18281C5.30009 5.06554 5.45915 4.99965 5.625 4.99965C5.79086 4.99965 5.94992 5.06554 6.06719 5.18281L8.125 7.24141L10.1828 5.18281C10.2409 5.12474 10.3098 5.07868 10.3857 5.04725C10.4616 5.01583 10.5429 4.99965 10.625 4.99965C10.7071 4.99965 10.7884 5.01583 10.8643 5.04725C10.9402 5.07868 11.0091 5.12474 11.0672 5.18281C11.1253 5.24088 11.1713 5.30982 11.2027 5.38569C11.2342 5.46156 11.2504 5.54288 11.2504 5.625C11.2504 5.70712 11.2342 5.78844 11.2027 5.86431C11.1713 5.94018 11.1253 6.00912 11.0672 6.06719ZM16.25 8.125C16.25 9.73197 15.7735 11.3029 14.8807 12.639C13.9879 13.9752 12.719 15.0166 11.2343 15.6315C9.74966 16.2465 8.11599 16.4074 6.5399 16.0939C4.9638 15.7804 3.51606 15.0065 2.37976 13.8702C1.24346 12.7339 0.469628 11.2862 0.156123 9.71011C-0.157382 8.13401 0.00352044 6.50035 0.618482 5.0157C1.23344 3.53105 2.27485 2.2621 3.611 1.36931C4.94714 0.476523 6.51803 0 8.125 0C10.2792 0.00227486 12.3445 0.85903 13.8677 2.38227C15.391 3.90551 16.2477 5.97081 16.25 8.125ZM15 8.125C15 6.76525 14.5968 5.43604 13.8414 4.30545C13.0859 3.17487 12.0122 2.29368 10.756 1.77333C9.49971 1.25298 8.11738 1.11683 6.78376 1.3821C5.45014 1.64737 4.22513 2.30216 3.26364 3.26364C2.30216 4.22513 1.64738 5.45013 1.3821 6.78375C1.11683 8.11737 1.25298 9.49971 1.77333 10.7559C2.29368 12.0122 3.17487 13.0859 4.30546 13.8414C5.43605 14.5968 6.76526 15 8.125 15C9.94773 14.9979 11.6952 14.2729 12.9841 12.9841C14.2729 11.6952 14.9979 9.94773 15 8.125Z"
                                    fill="#0E1F27"
                                  />
                                </svg>
                              </span>
                            }
                            onClose={() => handleTagClose(tag.name)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              margin: 0,
                              padding: '6px 12px',
                              borderRadius: 10,
                              border: '1px solid #E1E1E1',
                              background: '#FBFBF7',
                              color: '#344054',
                              fontSize: 14,
                              maxWidth: '100%',
                              whiteSpace: 'normal',
                              lineHeight: '20px',
                            }}
                          >
                            {tag.name}
                          </Tag>
                        ))}
                      </div>
                    )}
                    {addTaskForm.tags.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <Button
                          type="link"
                          onClick={() => {
                            setDescModalVisible(true);
                            setRoleList(addTaskForm.tags);
                          }}
                          style={{ padding: 0, height: 'auto', fontWeight: 500, color: '#0E1F27' }}
                        >
                          Edit function descriptions
                        </Button>
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <Select
                      mode="multiple"
                      placeholder="Search users to add..."
                      value={addTaskForm.member}
                      onChange={(e) => setAddTaskForm({ ...addTaskForm, member: e })}
                      style={{ width: '100%', minHeight: 46, fontSize: 16 }}
                      size="large"
                      maxTagCount={0}
                      showSearch
                      filterOption={(input, option) => {
                        const uid = (option as { value?: string })?.value;
                        const emp = employees.find((e) => String(e.uid) === String(uid));
                        const name = emp?.name ?? '';
                        return name.toLowerCase().includes(input.toLowerCase());
                      }}
                      dropdownStyle={{ zIndex: 1500 }}
                      getPopupContainer={() => document.body}
                      dropdownRender={(menu) => <div>{menu}</div>}
                    >
                      {employees.map((employee, index) => (
                        <Option key={index} value={employee.uid}>
                          {employee.name}
                        </Option>
                      ))}
                    </Select>
                    {addTaskForm.member?.length > 0 && (
                      <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {addTaskForm.member.map((uid: any) => {
                          const emp = employees.find((e) => String(e.uid) === String(uid));
                          const display = emp
                            ? `${emp.name}${emp.comments ? ` - ${emp.comments}` : emp.email ? ` - ${emp.email}` : ''}`
                            : String(uid);
                          return (
                            <Tag
                              key={String(uid)}
                              closable
                              closeIcon={
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 17,
                                    height: 17,
                                  }}
                                >
                                  <svg
                                    width="17"
                                    height="17"
                                    viewBox="0 0 17 17"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ display: 'block' }}
                                  >
                                    <path
                                      d="M11.0672 6.06719L9.0086 8.125L11.0672 10.1828C11.1253 10.2409 11.1713 10.3098 11.2027 10.3857C11.2342 10.4616 11.2504 10.5429 11.2504 10.625C11.2504 10.7071 11.2342 10.7884 11.2027 10.8643C11.1713 10.9402 11.1253 11.0091 11.0672 11.0672C11.0091 11.1253 10.9402 11.1713 10.8643 11.2027C10.7884 11.2342 10.7071 11.2503 10.625 11.2503C10.5429 11.2503 10.4616 11.2342 10.3857 11.2027C10.3098 11.1713 10.2409 11.1253 10.1828 11.0672L8.125 9.00859L6.06719 11.0672C6.00912 11.1253 5.94018 11.1713 5.86431 11.2027C5.78844 11.2342 5.70713 11.2503 5.625 11.2503C5.54288 11.2503 5.46156 11.2342 5.38569 11.2027C5.30982 11.1713 5.24088 11.1253 5.18282 11.0672C5.12475 11.0091 5.07868 10.9402 5.04726 10.8643C5.01583 10.7884 4.99966 10.7071 4.99966 10.625C4.99966 10.5429 5.01583 10.4616 5.04726 10.3857C5.07868 10.3098 5.12475 10.2409 5.18282 10.1828L7.24141 8.125L5.18282 6.06719C5.06554 5.94991 4.99966 5.79085 4.99966 5.625C4.99966 5.45915 5.06554 5.30009 5.18282 5.18281C5.30009 5.06554 5.45915 4.99965 5.625 4.99965C5.79086 4.99965 5.94992 5.06554 6.06719 5.18281L8.125 7.24141L10.1828 5.18281C10.2409 5.12474 10.3098 5.07868 10.3857 5.04725C10.4616 5.01583 10.5429 4.99965 10.625 4.99965C10.7071 4.99965 10.7884 5.01583 10.8643 5.04725C10.9402 5.07868 11.0091 5.12474 11.0672 5.18281C11.1253 5.24088 11.1713 5.30982 11.2027 5.38569C11.2342 5.46156 11.2504 5.54288 11.2504 5.625C11.2504 5.70712 11.2342 5.78844 11.2027 5.86431C11.1713 5.94018 11.1253 6.00912 11.0672 6.06719ZM16.25 8.125C16.25 9.73197 15.7735 11.3029 14.8807 12.639C13.9879 13.9752 12.719 15.0166 11.2343 15.6315C9.74966 16.2465 8.11599 16.4074 6.5399 16.0939C4.9638 15.7804 3.51606 15.0065 2.37976 13.8702C1.24346 12.7339 0.469628 11.2862 0.156123 9.71011C-0.157382 8.13401 0.00352044 6.50035 0.618482 5.0157C1.23344 3.53105 2.27485 2.2621 3.611 1.36931C4.94714 0.476523 6.51803 0 8.125 0C10.2792 0.00227486 12.3445 0.85903 13.8677 2.38227C15.391 3.90551 16.2477 5.97081 16.25 8.125ZM15 8.125C15 6.76525 14.5968 5.43604 13.8414 4.30545C13.0859 3.17487 12.0122 2.29368 10.756 1.77333C9.49971 1.25298 8.11738 1.11683 6.78376 1.3821C5.45014 1.64737 4.22513 2.30216 3.26364 3.26364C2.30216 4.22513 1.64738 5.45013 1.3821 6.78375C1.11683 8.11737 1.25298 9.49971 1.77333 10.7559C2.29368 12.0122 3.17487 13.0859 4.30546 13.8414C5.43605 14.5968 6.76526 15 8.125 15C9.94773 14.9979 11.6952 14.2729 12.9841 12.9841C14.2729 11.6952 14.9979 9.94773 15 8.125Z"
                                      fill="#0E1F27"
                                    />
                                  </svg>
                                </span>
                              }
                              onClose={() => {
                                setAddTaskForm((prev: any) => ({
                                  ...prev,
                                  member: prev.member.filter((m: any) => String(m) !== String(uid)),
                                }));
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                margin: 0,
                                padding: '6px 12px',
                                borderRadius: 10,
                                border: '1px solid #E1E1E1',
                                background: '#FBFBF7',
                                color: '#344054',
                                fontSize: 14,
                                maxWidth: '100%',
                                whiteSpace: 'normal',
                                lineHeight: '20px',
                              }}
                            >
                              {display}
                            </Tag>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 14,
                  marginTop: 16,
                  paddingTop: 8,
                  paddingBottom: isMobile ? 8 : 4,
                  flexShrink: 0,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  disabled={submitLoading}
                  onClick={() => {
                    setAddTaskDrawerVisible(false);
                    setAddTaskForm({ title: '', comments: '', member: [], task_id: '', need_role: '1', tags: [] });
                    setInputValue('');
                  }}
                  style={{
                    height: 48,
                    paddingLeft: 28,
                    paddingRight: 28,
                    borderRadius: 999,
                    border: '1px solid #E4E7EC',
                    background: '#FFFFFF',
                    color: '#171717',
                    fontWeight: 500,
                    fontSize: 16,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleAddNewTask}
                  loading={submitLoading}
                  size="large"
                  style={{
                    minWidth: isMobile ? 168 : 200,
                    height: 48,
                    paddingLeft: 24,
                    paddingRight: 18,
                    borderRadius: 999,
                    border: 'none',
                    background:
                      'radial-gradient(76.89% 119.16% at 50% -19.12%, #102D3D 0%, #0E1F27 100%), #0E1F27',
                    boxShadow: '0 9px 20px 0 rgba(14, 31, 39, 0.2)',
                  }}
                >
                  {!submitLoading && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <span>{isEditing ? 'Save Changes' : 'Add project'}</span>
                      <span
                        style={{
                          width: 24,
                          height: 24,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M12 0C9.62663 0 7.30655 0.703788 5.33316 2.02236C3.35977 3.34094 1.8217 5.21508 0.913451 7.4078C0.00519941 9.60051 -0.232441 12.0133 0.230582 14.3411C0.693605 16.6689 1.83649 18.807 3.51472 20.4853C5.19295 22.1635 7.33115 23.3064 9.65892 23.7694C11.9867 24.2324 14.3995 23.9948 16.5922 23.0865C18.7849 22.1783 20.6591 20.6402 21.9776 18.6668C23.2962 16.6934 24 14.3734 24 12C23.9966 8.81843 22.7313 5.76814 20.4816 3.51843C18.2319 1.26872 15.1816 0.00335979 12 0ZM17.2685 9.88384L10.8069 16.3454C10.7212 16.4312 10.6194 16.4993 10.5073 16.5457C10.3953 16.5922 10.2752 16.6161 10.1538 16.6161C10.0325 16.6161 9.91243 16.5922 9.80037 16.5457C9.68831 16.4993 9.5865 16.4312 9.50077 16.3454L6.73154 13.5762C6.55834 13.4029 6.46103 13.168 6.46103 12.9231C6.46103 12.6781 6.55834 12.4432 6.73154 12.27C6.90475 12.0968 7.13967 11.9995 7.38462 11.9995C7.62957 11.9995 7.86449 12.0968 8.0377 12.27L10.1538 14.3873L15.9623 8.57769C16.0481 8.49193 16.1499 8.4239 16.2619 8.37748C16.374 8.33107 16.4941 8.30718 16.6154 8.30718C16.7367 8.30718 16.8568 8.33107 16.9688 8.37748C17.0809 8.4239 17.1827 8.49193 17.2685 8.57769C17.3542 8.66345 17.4223 8.76527 17.4687 8.87732C17.5151 8.98938 17.539 9.10948 17.539 9.23077C17.539 9.35206 17.5151 9.47216 17.4687 9.58421C17.4223 9.69627 17.3542 9.79808 17.2685 9.88384Z"
                            fill="white"
                          />
                        </svg>
                      </span>
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        title="Add Description"
        visible={descModalVisible}
        zIndex={2000}
        centered
        destroyOnClose
        onCancel={() => setDescModalVisible(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setDescModalVisible(false)}
            type="text"
            style={{ color: '#171717', fontWeight: 500 }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitLoading}
            onClick={setInputConfirm}
            style={{
              background: '#0E1F27',
              borderColor: '#0E1F27',
              color: '#fff',
              borderRadius: 999,
              boxShadow: '0 9px 20px 0 rgba(14, 31, 39, 0.25)',
              paddingLeft: 28,
              paddingRight: 28,
            }}
          >
            Add
          </Button>,
        ]}
        bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
      >
        {roleList.map((role, index) => (
          <div key={index} style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{role.name}</div>
            <Input
              placeholder={`Enter description for ${role.name}`}
              value={role.description}
              onChange={(e) => {
                const newList = [...roleList];
                newList[index].description = e.target.value;
                setRoleList(newList);
              }}
              maxLength={100}
              showCount
            />
          </div>
        ))}
      </Modal>

    </div>
  );
};

export default TaskList;
